import { useState } from 'react';
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ProcessorEditHeader } from '../../components/processor-edit-header';

import { ProcessorDetails, useProcessorById, useUpdateProcessor } from '@/lib/api/processors';
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { SecretInput } from '@/components/form/secret-input';
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id';
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit';

interface CCAvenueEditPageProps {
  processor: ProcessorDetails;
}

const CCAvenueEditPageContainerInner = () => {
  const { t } = useTranslation('processors');
  const id = useProcessorEditId();
  const { data, isLoading, error } = useProcessorById(id!);

  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorDisplay error={parseTRPCError(error)} />;
  }

  if (!data) {
    return <div className="container mx-auto py-8">{t('processorNotFound')}</div>;
  }

  return <CCAvenueEditPage processor={data} />;
};

const CCAvenueEditPage = ({ processor }: CCAvenueEditPageProps) => {
  const { t } = useTranslation('processors');
  const navigate = useNavigate();
  const processorExit = useProcessorEditExit();
  const { toast } = useToast();

  const [validationError, setValidationError] = useState<string | null>(null);

  const updateProcessorMutation = useUpdateProcessor();

  const [formData, setFormData] = useState({
    name: processor.name || '',
    enabled: processor.enabled ?? true,
    dryMode: (processor.options as any)?.dryMode === true,
    merchantId: (processor.options as any)?.merchantId || '',
    accessCode: (processor.options as any)?.accessCode || '',
    workingKey: (processor.options as any)?.workingKey || '',
  });

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.merchantId) {
      setValidationError(t('ccavenue.credentials.merchantIdRequired'));
      return;
    }

    if (!formData.accessCode) {
      setValidationError(t('ccavenue.credentials.accessCodeRequired'));
      return;
    }

    if (!formData.workingKey) {
      setValidationError(t('ccavenue.credentials.workingKeyRequired'));
      return;
    }

    try {
      await updateProcessorMutation.mutateAsync({
        processorId: processor.id,
        updatedData: {
          type: 'ccavenue',
          name: formData.name,
          enabled: formData.enabled,
          options: {
            dryMode: formData.dryMode,
            merchantId: formData.merchantId,
            accessCode: formData.accessCode,
            workingKey: formData.workingKey,
          },
        } as any,
      });

      toast({ description: t('processorUpdated') });
      if (processorExit.isEmbed) {
        processorExit.exitAfterSave();
        return;
      }
      navigate(-1);
    } catch (error) {
      console.error('Error saving processor:', error);
      toast({ description: t('failedToSave'), variant: 'destructive' });
    }
  };

  return (
    <div className="container mx-auto py-8">
      <ProcessorEditHeader
        processorId={processor.id}
        title={t('ccavenue.editTitle')}
        onCancel={() => (processorExit.isEmbed ? processorExit.exitCancel() : navigate(-1))}
        onSave={handleSubmit}
        isSaving={updateProcessorMutation.isPending}
      />

      {validationError && (
        <Alert variant="destructive" className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        {/* General Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('generalSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">{t('name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('enterProcessorName')}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked === true })}
              />
              <Label htmlFor="enabled">{t('enabled')}</Label>
            </div>
          </CardContent>
        </Card>

        {/* CCAvenue Credentials */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('ccavenue.credentials.merchantIdLabel')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="merchantId">{t('ccavenue.credentials.merchantIdLabel')} *</Label>
              <Input
                id="merchantId"
                value={formData.merchantId}
                onChange={(e) => setFormData({ ...formData, merchantId: e.target.value })}
                placeholder={t('ccavenue.credentials.merchantIdPlaceholder')}
              />
              <p className="text-muted-foreground mt-1 text-xs">
                {t('ccavenue.credentials.merchantIdDescription')}
              </p>
            </div>

            <div>
              <SecretInput
                label={t('ccavenue.credentials.accessCodeLabel')}
                value={formData.accessCode}
                onChange={(value) => setFormData({ ...formData, accessCode: value })}
                placeholder={t('ccavenue.credentials.accessCodePlaceholder')}
              />
              <p className="text-muted-foreground mt-1 text-xs">
                {t('ccavenue.credentials.accessCodeDescription')}
              </p>
            </div>

            <div>
              <SecretInput
                label={t('ccavenue.credentials.workingKeyLabel')}
                value={formData.workingKey}
                onChange={(value) => setFormData({ ...formData, workingKey: value })}
                placeholder={t('ccavenue.credentials.workingKeyPlaceholder')}
              />
              <p className="text-muted-foreground mt-1 text-xs">
                {t('ccavenue.credentials.workingKeyDescription')}
              </p>
            </div>
          </CardContent>
        </Card>

                <ProcessorDryModeField
                    checked={formData.dryMode}
                    onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
                    disabled={updateProcessorMutation.isPending}
                />
      </div>
    </div>
  );
};

export const CCAvenueEditPageContainer = withRequiredPermission(CCAvenueEditPageContainerInner, PERMISSIONS.PROCESSORS_MANAGE);
